import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { useCallback, useEffect, useRef } from 'react';
import { promiseDeprecatedFromLocalService, promiseDeprecatedFromLocalServicePure, pureServiceKey } from '../../core/constants';
import { isAsync, isModel } from '../../tools';
import { runtimePromiseDeprecatedVarAssignment } from './useCloneModel';
var cache = new Map();
var cacheDeps = new Map();
var depsDifferent = function depsDifferent() {
  var _source$length;
  var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if ((source !== null && source !== void 0 ? source : []).length !== (target !== null && target !== void 0 ? target : []).length) return true;
  var l = (_source$length = source === null || source === void 0 ? void 0 : source.length) !== null && _source$length !== void 0 ? _source$length : 0;
  for (var i = 0; i < l; i += 1) {
    if (!Object.is(source[i], target[i])) {
      return true;
    }
  }
  return false;
};
var getDifferentIndex = function getDifferentIndex() {
  var source = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var target = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var sl = source.length;
  var tl = target.length;
  var max = Math.max(sl, tl);
  var min = Math.min(sl, tl);
  var res = [];
  var i = 0;
  for (; i < min; i += 1) {
    if (!Object.is(source[i], target[i])) {
      res.push(i);
    }
  }
  for (; i < max; i += 1) {
    res.push(i);
  }
  return res;
};
var useService = function useService(model, clonedModel, service, deps, options) {
  var _ref = options || {},
    suspenseKey = _ref.suspenseKey,
    suspense = _ref.suspense,
    control = _ref.control,
    autoLoad = _ref.autoLoad;
  var susKey = suspenseKey || (suspense === null || suspense === void 0 ? void 0 : suspense.key);
  var depsRef = useRef(deps);
  var serviceRef = useRef(service);
  serviceRef.current = service;
  var clonedModelRef = useRef(clonedModel);
  clonedModelRef.current = clonedModel;
  var modelRef = useRef(model);
  modelRef.current = model;
  var localServicePure = useCallback(function (data) {
    var _serviceRef$current;
    var state = modelRef.current();
    if (!serviceRef.current) return Promise.resolve(state);
    var r = (_serviceRef$current = serviceRef.current) === null || _serviceRef$current === void 0 ? void 0 : _serviceRef$current.call(serviceRef, state, data);
    return runtimePromiseDeprecatedVarAssignment(function () {
      return modelRef.current.race(r);
    }, promiseDeprecatedFromLocalServicePure);
  }, []);
  var localServiceHasStatus = useCallback(function (data) {
    var _serviceRef$current2;
    var state = clonedModelRef.current();
    if (!serviceRef.current) return Promise.resolve(state);
    var r = (_serviceRef$current2 = serviceRef.current) === null || _serviceRef$current2 === void 0 ? void 0 : _serviceRef$current2.call(serviceRef, state, data);
    return runtimePromiseDeprecatedVarAssignment(function () {
      return clonedModelRef.current.__race__(r);
    }, promiseDeprecatedFromLocalService);
  }, []);
  if (!localServiceHasStatus[pureServiceKey]) {
    localServiceHasStatus[pureServiceKey] = localServicePure;
  }
  var firstRenderFlagRef = useRef(true);
  var susPersist = suspense === null || suspense === void 0 ? void 0 : suspense.persist;
  var depsIsDifferent = false;
  var promise;
  if (susKey && cache.has(susKey)) {
    promise = cache.get(susKey);
    var dps = cacheDeps.get(susKey);
    depsIsDifferent = depsDifferent(dps, deps);
    if (!depsIsDifferent) {
      if (promise.success) {
        clonedModel.silent(promise.data);
        if (firstRenderFlagRef.current) {
          firstRenderFlagRef.current = false;
        }
      } else if (promise.success === false) {
        if (firstRenderFlagRef.current) {
          firstRenderFlagRef.current = false;
        }
      } else {
        throw promise;
      }
    }
  }
  if (firstRenderFlagRef.current && typeof service === 'function' && !isModel(control)) {
    firstRenderFlagRef.current = false;
    if (autoLoad) {
      var result = service(clonedModel(), undefined, []);
      if (isAsync(result)) {
        if (susKey) {
          var p = clonedModel.race(result).then(function (data) {
            p.success = true;
            p.data = data;
          }).catch(function (err) {
            p.success = false;
            return Promise.reject(err);
          });
          cache.set(susKey, p);
          cacheDeps.set(susKey, deps);
          throw p;
        }
        clonedModel.race(result);
      } else if (depsIsDifferent) {
        var _promise, _promise2;
        if ((_promise = promise) !== null && _promise !== void 0 && _promise.success) {
          clonedModel.silent(promise.data);
        } else if (((_promise2 = promise) === null || _promise2 === void 0 ? void 0 : _promise2.success) === undefined) {
          throw promise;
        }
      } else {
        clonedModel.race(result);
      }
    }
  }
  if (depsDifferent(depsRef.current, deps)) {
    if (susKey && susPersist && cache.has(susKey) && !depsIsDifferent) {
      cache.delete(susKey);
    } else if (typeof service === 'function') {
      if (autoLoad) {
        var _result = service(clonedModel(), undefined, getDifferentIndex(depsRef.current, deps));
        if (isAsync(_result)) {
          var _p = clonedModel.race(_result);
          if (susPersist && susKey) {
            var tmpP = _p.then(function (data) {
              tmpP.success = true;
              tmpP.data = data;
            }).catch(function (err) {
              tmpP.success = false;
              return Promise.reject(err);
            });
            cache.set(susKey, tmpP);
            cacheDeps.set(susKey, deps);
            throw tmpP;
          }
        } else if (depsIsDifferent) {
          cache.delete(susKey);
          clonedModel.race(_result);
        } else {
          clonedModel.race(_result);
        }
      }
    }
    depsRef.current = _toConsumableArray(deps || []);
  } else if (susKey && cache.has(susKey)) {
    cache.delete(susKey);
  }
  useEffect(function () {
    return function () {
      cache.delete(susKey);
      cacheDeps.delete(susKey);
    };
  }, []);
  return [localServiceHasStatus];
};
export default useService;